import { useEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { useModal } from '@/lib/ModalContext';
import { submit, captureLead, type FormKind } from '@/lib/api';
import styles from './Modal.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Modal({ kind, children }: { kind: FormKind; children: ReactNode }) {
  const { open, setOpen } = useModal();
  const isOpen = open === kind;
  return (
    <div
      className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}
    >
      <div className={styles.modal}>
        <button className={styles.close} aria-label="Close" onClick={() => setOpen(null)}>×</button>
        {children}
      </div>
    </div>
  );
}

export function WaitlistModal() {
  const { setOpen } = useModal();
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState('submitting');
    const fd = new FormData(e.currentTarget);
    const r = await submit('waitlist', Object.fromEntries(fd));
    if (r.ok) {
      setState('success'); setMessage(r.message);
      (e.target as HTMLFormElement).reset();
    } else {
      setState('error'); setMessage(`Couldn't submit (${r.error}). Email hello@avowal.ai instead.`);
    }
    setTimeout(() => { if (state !== 'submitting') setOpen(null); }, 1800);
  }

  return (
    <Modal kind="waitlist">
      <div className={styles.eyebrow}>/ Waitlist</div>
      <h3 className={styles.title}>Join the waitlist</h3>
      <p className={styles.sub}>
        Get priority access when private beta opens. We'll only email you when the doors open or when something materially changes.
      </p>
      <form onSubmit={onSubmit}>
        <div className={styles.field}>
          <label htmlFor="w-email">Email</label>
          <input type="email" id="w-email" name="email" required autoComplete="email" placeholder="you@example.com" />
        </div>
        <div className={styles.field}>
          <label htmlFor="w-state">State (optional)</label>
          <input type="text" id="w-state" name="state" autoComplete="address-level1" placeholder="CT" />
        </div>
        <div className={styles.field}>
          <label htmlFor="w-role">I am a&hellip;</label>
          <select id="w-role" name="role" defaultValue="potential_client">
            <option value="potential_client">Potential client</option>
            <option value="attorney">Licensed attorney</option>
            <option value="press">Press / analyst</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button type="submit" className={styles.submit} disabled={state === 'submitting'}>
          {state === 'submitting' ? 'Submitting…' : 'Join waitlist →'}
        </button>
        {state === 'success' && <div className={`${styles.msg} ${styles.success}`}>{message}</div>}
        {state === 'error'   && <div className={`${styles.msg} ${styles.error}`}>{message}</div>}
      </form>
    </Modal>
  );
}

export function IntakeModal() {
  const { open, setOpen } = useModal();
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const doneRef = useRef(false);

  function readFields() {
    const fd = new FormData(formRef.current!);
    return {
      email: String(fd.get('email') ?? ''),
      jurisdiction: String(fd.get('state') ?? ''),
      matterType: String(fd.get('matter_type') ?? ''),
      summary: String(fd.get('summary') ?? ''),
    };
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState('submitting');
    const r = await captureLead(readFields(), true);
    if (r.ok) {
      doneRef.current = true;
      setState('success'); setMessage(r.message);
      (e.target as HTMLFormElement).reset();
      setTimeout(() => setOpen(null), 2400);
    } else {
      setState('error'); setMessage(`Couldn't submit (${r.error}). Email hello@avowal.ai instead.`);
    }
  }

  // Abandoned-lead capture: when the modal closes with a valid email entered but
  // no successful submit, persist whatever was filled as a draft so the lead
  // survives. Best-effort and fire-and-forget — never blocks the close.
  const wasOpen = useRef(false);
  useEffect(() => {
    const isOpen = open === 'intake';
    if (wasOpen.current && !isOpen && !doneRef.current && formRef.current) {
      const f = readFields();
      if (EMAIL_RE.test(f.email.trim())) void captureLead(f, false);
    }
    if (isOpen) { doneRef.current = false; setState('idle'); setMessage(''); }
    wasOpen.current = isOpen;
  }, [open]);

  return (
    <Modal kind="intake">
      <div className={styles.eyebrow}>/ Get matched</div>
      <h3 className={styles.title}>Tell us about your matter</h3>
      <p className={styles.sub}>
        Three short fields. ELARIA reviews intake first &mdash; you'll get a response within one business
        day with either a match or an honest "we can't help with this yet."
      </p>
      <form ref={formRef} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label htmlFor="i-email">Email</label>
          <input type="email" id="i-email" name="email" required autoComplete="email" placeholder="you@example.com" />
        </div>
        <div className={styles.field}>
          <label htmlFor="i-state">State / jurisdiction</label>
          <input type="text" id="i-state" name="state" required placeholder="e.g. Connecticut, Superior Court" />
        </div>
        <div className={styles.field}>
          <label htmlFor="i-matter">Matter type</label>
          <select id="i-matter" name="matter_type" required defaultValue="">
            <option value="" disabled>Select…</option>
            <option value="family">Family / dissolution</option>
            <option value="contract">Contract dispute</option>
            <option value="small_claims">Small claims</option>
            <option value="landlord_tenant">Landlord / tenant</option>
            <option value="employment">Employment</option>
            <option value="estate">Estate / probate</option>
            <option value="appellate">Appellate</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor="i-summary">One-line summary</label>
          <textarea id="i-summary" name="summary" required placeholder="What do you need help with? Plain English is fine." />
        </div>
        <button type="submit" className={styles.submit} disabled={state === 'submitting'}>
          {state === 'submitting' ? 'Submitting…' : 'Submit intake →'}
        </button>
        {state === 'success' && <div className={`${styles.msg} ${styles.success}`}>{message}</div>}
        {state === 'error'   && <div className={`${styles.msg} ${styles.error}`}>{message}</div>}
      </form>
    </Modal>
  );
}

// Mount both modals once at app root; they show themselves based on context.
export function Modals() {
  // Add Esc + scroll-lock effects scoped at this top-level mount.
  const { open } = useModal();
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  return <><WaitlistModal /><IntakeModal /></>;
}
